import clsx from 'clsx';
import { useEffect, useId, useMemo, useRef } from 'react';

export type CheckboxProps = {
    label?: string | React.ReactNode;
    name?: string;
    cRef?: React.Ref<HTMLInputElement>;
    checked?: boolean;
    onChange: (checked: boolean) => void;
    value?: string | number;
    labelAlignment?: 'top' | 'center';
    type?: 'checkbox' | 'radio';
    disabled?: boolean;
    deselectable?: boolean;
    showRequiredError?: boolean;
    triggerChangeOnFocus?: boolean;
    brandColor?: string;
};

export const Checkbox = ({
    onChange,
    label,
    cRef = useRef<HTMLInputElement>(null),
    checked,
    name,
    value,
    labelAlignment,
    type = 'checkbox',
    disabled = false,
    deselectable = false,
    showRequiredError = false,
    triggerChangeOnFocus = true,
    brandColor,
}: CheckboxProps) => {
    const containerRef = useRef<HTMLLabelElement>(null);
    const htmlName = name ? name?.replace(/[^a-z0-9-_]/g, '').toLocaleLowerCase() : useId();

    const onChangeCallback = () => {
        if (disabled) {
            return;
        }

        onChange(type === 'radio' ? true : !checked);
    };

    useEffect(() => {
        containerRef.current?.addEventListener('keydown', keyDown);

        return () => {
            containerRef.current?.removeEventListener('keydown', keyDown);
        };
    });

    const brandStyle = useMemo(
        () =>
            brandColor
                ? {
                      accentColor: brandColor,
                  }
                : {},
        [brandColor]
    );

    const keyDown = (e: KeyboardEvent) => {
        if (e.key !== 'Enter') {
            return;
        }

        const target = e?.target as HTMLElement;
        if (target?.tagName?.toLowerCase() === 'a') {
            return;
        }

        e.preventDefault();
        e.stopPropagation();
        onChange(!checked);
    };

    return (
        <label
            ref={containerRef}
            htmlFor={htmlName}
            data-component="checkbox"
            onClick={onChangeCallback}
            className={clsx('r-flex r-gap-4 r-rounded-lg hover:r-cursor-pointer', {
                'hover:r-cursor-pointer': !disabled,
                'r-items-center': labelAlignment === 'center',
                'r-bg-red-100 r-p-1': showRequiredError,
            })}
        >
            <input
                ref={cRef}
                // id={htmlName}
                name={htmlName}
                type={type}
                value={value}
                checked={checked}
                disabled={disabled}
                onClick={() => {
                    if (type === 'radio' && deselectable && checked) {
                        onChange(false);
                    }
                }}
                onFocus={() => {
                    if (type === 'radio' && triggerChangeOnFocus) {
                        onChangeCallback();
                    }
                }}
                onChange={onChangeCallback}
                className="r-h-6 r-w-6 r-flex-shrink-0 r-border-none r-accent-primary-300 focus-visible:r-rounded-full focus-visible:r-outline-none focus-visible:r-ring-1 focus-visible:r-ring-blue-800 focus-visible:r-ring-offset-2"
                style={brandStyle}
            />
            {label}
        </label>
    );
};
